// src/components/common/HotelScopeSelector.tsx
//
// Multi-hotel scope selector (mockup 05, callout 7 / Decision 6): a pill that
// opens a checklist popover — search box, a tri-state "select entire group"
// row per group, individual hotel checkboxes, and a footer with the selected
// count plus «Todos» and «Desmarcar todos» shortcuts. An empty selection is
// never applied (it would read as «0 hoteles» while every screen showed all of
// them): «Desmarcar todos» only clears the checklist, and the first hotel or
// group ticked afterwards becomes the selection. The selection is owned by
// useHotelScope() (backed by the preferences store).
import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  InputAdornment,
  Popover,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ApartmentRounded as ScopeIcon,
  ExpandMoreRounded as ChevronIcon,
  SearchRounded as SearchIcon,
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import type { HotelScope } from '@/hooks/useHotelScope';

interface HotelScopeChecklistProps {
  scope: HotelScope;
}

/**
 * The checklist body only (search + tri-state group rows + hotel checkboxes +
 * footer) with no trigger button of its own — for
 * screens that already have a scope pill/popover shell (e.g. the legacy
 * Dashboard's FilterToolbar) and just need the new multi-select content
 * dropped inside it.
 */
export const HotelScopeChecklist: React.FC<HotelScopeChecklistProps> = ({ scope }) => {
  const translate = useTranslate();
  const [search, setSearch] = useState('');
  // «Desmarcar todos» pending: the checklist shows nothing ticked while the
  // applied selection stays as it was until a hotel or group is ticked.
  const [cleared, setCleared] = useState(false);

  const { groups, allHotels, selectedHotelIds, setSelectedHotelIds } = scope;

  const selectedSet = useMemo(
    () => (cleared ? new Set<string>() : new Set(selectedHotelIds ?? allHotels.map((h) => h.id))),
    [cleared, selectedHotelIds, allHotels]
  );

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return groups;
    const term = search.trim().toLowerCase();
    return groups
      .map((g) => ({ ...g, hotels: g.hotels.filter((h) => h.name.toLowerCase().includes(term)) }))
      .filter((g) => g.hotels.length > 0);
  }, [groups, search]);

  const apply = (next: Set<string>) => {
    // At least one hotel always stays selected.
    if (next.size === 0) return;
    setCleared(false);
    setSelectedHotelIds(next.size === allHotels.length ? null : Array.from(next));
  };

  const toggleHotel = (hotelId: string) => {
    const next = new Set(selectedSet);
    if (next.has(hotelId)) next.delete(hotelId);
    else next.add(hotelId);
    apply(next);
  };

  const toggleGroup = (groupHotelIds: string[]) => {
    const allSelected = groupHotelIds.every((id) => selectedSet.has(id));
    const next = new Set(selectedSet);
    if (allSelected) {
      groupHotelIds.forEach((id) => next.delete(id));
    } else {
      groupHotelIds.forEach((id) => next.add(id));
    }
    apply(next);
  };

  const selectAll = () => {
    setCleared(false);
    setSelectedHotelIds(null);
  };

  return (
    <Box sx={{ width: 320 }}>
      <TextField
        fullWidth
        size="small"
        placeholder={translate('scope.search.placeholder')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1 }}
      />

      <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
        {filteredGroups.map((group) => {
          const groupHotelIds = group.hotels.map((h) => h.id);
          const selectedCount = groupHotelIds.filter((id) => selectedSet.has(id)).length;
          const allSelected = selectedCount === groupHotelIds.length && groupHotelIds.length > 0;
          const someSelected = selectedCount > 0 && !allSelected;

          return (
            <Box key={group.id} sx={{ mb: 0.5 }}>
              {groups.length > 1 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={() => toggleGroup(groupHotelIds)}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {group.name} <Typography component="span" variant="body2" color="text.secondary">({groupHotelIds.length})</Typography>
                  </Typography>
                </Box>
              )}
              {group.hotels.map((hotel) => (
                <Box key={hotel.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: groups.length > 1 ? 2 : 0 }}>
                  <Checkbox size="small" checked={selectedSet.has(hotel.id)} onChange={() => toggleHotel(hotel.id)} />
                  <Typography variant="body2">{hotel.name}</Typography>
                </Box>
              ))}
            </Box>
          );
        })}
      </Box>

      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">
          {cleared ? translate('scope.footer.pickOne') : translate('scope.footer.selectedCount', { count: selectedSet.size })}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button size="small" onClick={() => setCleared(true)} disabled={cleared} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
            {translate('scope.footer.clear')}
          </Button>
          <Button size="small" onClick={selectAll} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
            {translate('scope.footer.all')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

// The selector used to carry a «Solo con llegadas» switch: it was saved but never applied (the
// list of hotels does not know the period), so it was removed. Instalaciones keeps its own,
// which the server applies.
interface HotelScopeSelectorProps {
  scope: HotelScope;
}

/**
 * Full control: header pill («Todos los hoteles» / «1 hotel» / «N hoteles»)
 * plus the checklist popover. This is what new pages (Llegadas de hoy,
 * Reservas, Resumen, Canales, Tiempos, Cobros, Hoteles) should render.
 */
export const HotelScopeSelector: React.FC<HotelScopeSelectorProps> = ({ scope }) => {
  const translate = useTranslate();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { allHotels, summary, loading } = scope;
  const open = Boolean(anchorEl);

  if (loading || allHotels.length <= 1) {
    return null;
  }

  return (
    <>
      <Button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        size="small"
        startIcon={<ScopeIcon sx={{ fontSize: 16 }} />}
        endIcon={<ChevronIcon sx={{ fontSize: 16 }} />}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.8rem',
          color: theme.palette.text.primary,
          borderRadius: theme.custom?.radius?.small ?? '6px',
          border: `1px solid ${theme.palette.divider}`,
          px: 1.25,
        }}
      >
        {translate(summary.key, summary.params as Record<string, unknown> | undefined)}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 1.5 } } }}
      >
        <HotelScopeChecklist scope={scope} />
      </Popover>
    </>
  );
};
